import type { TeamId } from './game';
import type { Vec3 } from './math';
import { vec3 } from './math';
import type { RouteType, RouteLandmarkKind, RouteLandmark } from './routeMetadata';
import { terrainHeightAt, terrainHeightAtDangerousCrossing, terrainHeightAtRaindance } from './terrain';

export type MapId = 'katabatic' | 'dangerouscrossing' | 'raindance';

export type RouteMetadataMap = {
  id: string;
  type: RouteType;
  label: string;
  description: string;
  team: TeamId;
  waypoints: Vec3[];
  landmarks: RouteLandmark[];
  difficultyRating: 1 | 2 | 3;
  expectedSpeedRange: [number, number];
};

// Phase 131-135 — weather & lighting variants per map (atmosphere round)
export type MapWeather = 'none' | 'snow' | 'sand' | 'rain';
export type MapTimeOfDay = 'day' | 'sunset' | 'night' | 'dawn';
// Phase 136 — terrain palette tokens map to shader tints
export type MapTerrainPalette = 'arctic' | 'desert' | 'verdant';

// Phase 138 — briefing copy + recommended loadout shown on map load
export type MapBriefing = {
  factionLeft: string;
  factionRight: string;
  blurb: string;
  recommendedLoadout: string;
};

export type MapData = {
  id: MapId;
  label: string;
  description: string;
  boundsRadius: number;
  // Phase 131-135 — atmosphere knobs
  weather?: MapWeather;
  timeOfDay?: MapTimeOfDay;
  terrainPalette?: MapTerrainPalette;
  // Phase 138 — briefing data
  briefing?: MapBriefing;
  // Phase 140 — distant backdrop tint
  haze?: { tint: string; opacity: number };
  visualProfile: {
    terrain: {
      snow: string;
      ice: string;
      rock: string;
      route: string;
    };
    sky: {
      fog: string;
      ambient: string;
      hemisphereSky: string;
      hemisphereGround: string;
      sun: string;
      horizon: string;
    };
    routeTone: string;
    landmarkTone: string;
    baseSilhouette: 'compact-spires' | 'canyon-shelves';
  };
  flagPositions: Record<TeamId, Vec3>;
  spawnPositions: Record<TeamId, Vec3[]>;
  spawnViewTargets: Record<TeamId, Vec3[]>;
  generatorPositions: Record<TeamId, Vec3>;
  routes: RouteMetadataMap[];
  baseProfile: {
    elevation: number;
    spread: number;
  };
};

export const DEFAULT_MAP_ID: MapId = 'katabatic';
export const MAP_IDS: MapId[] = ['katabatic', 'dangerouscrossing', 'raindance'];

function anchorK(x: number, z: number, lift = 2): Vec3 {
  return vec3(x, Math.max(terrainHeightAt(x, z) + lift, 12), z);
}

function socketK(x: number, z: number, lift = 1): Vec3 {
  return vec3(x, terrainHeightAt(x, z) + lift, z);
}

function wpK(x: number, z: number): Vec3 {
  return vec3(x, terrainHeightAt(x, z), z);
}

function landmarkK(x: number, z: number, label: string, kind: RouteLandmarkKind): RouteLandmark {
  return { position: vec3(x, terrainHeightAt(x, z), z), label, kind };
}

function anchorDC(x: number, z: number, lift = 2): Vec3 {
  return vec3(x, Math.max(terrainHeightAtDangerousCrossing(x, z) + lift, 12), z);
}

function socketDC(x: number, z: number, lift = 1): Vec3 {
  return vec3(x, terrainHeightAtDangerousCrossing(x, z) + lift, z);
}

function wpDC(x: number, z: number): Vec3 {
  return vec3(x, terrainHeightAtDangerousCrossing(x, z), z);
}

function landmarkDC(x: number, z: number, label: string, kind: RouteLandmarkKind): RouteLandmark {
  return { position: vec3(x, terrainHeightAtDangerousCrossing(x, z), z), label, kind };
}

function anchorRD(x: number, z: number, lift = 2): Vec3 {
  return vec3(x, Math.max(terrainHeightAtRaindance(x, z) + lift, 12), z);
}

function socketRD(x: number, z: number, lift = 1): Vec3 {
  return vec3(x, terrainHeightAtRaindance(x, z) + lift, z);
}

function wpRD(x: number, z: number): Vec3 {
  return vec3(x, terrainHeightAtRaindance(x, z), z);
}

function landmarkRD(x: number, z: number, label: string, kind: RouteLandmarkKind): RouteLandmark {
  return { position: vec3(x, terrainHeightAtRaindance(x, z), z), label, kind };
}

function buildRaindanceRoutes(): RouteMetadataMap[] {
  return [
    {
      id: 'rd-red-main',
      type: 'main',
      label: 'Red Main',
      description: 'Red cliff to blue stand via the Raindance bridge. Fast but exposed.',
      team: 'red',
      waypoints: [
        wpRD(-95, 0), wpRD(-70, -8), wpRD(-40, -4), wpRD(0, 0), wpRD(40, 4), wpRD(70, 8), wpRD(95, 0),
      ],
      landmarks: [
        landmarkRD(-95, 0, 'Red Spawn Exit', 'checkpoint'),
        landmarkRD(0, 0, 'Raindance Bridge', 'sightline'),
        landmarkRD(95, 0, 'Blue Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [28, 42],
    },
    {
      id: 'rd-blue-main',
      type: 'main',
      label: 'Blue Main',
      description: 'Blue cliff to red stand across the narrow bridge.',
      team: 'blue',
      waypoints: [
        wpRD(95, 0), wpRD(70, 8), wpRD(40, 4), wpRD(0, 0), wpRD(-40, -4), wpRD(-70, -8), wpRD(-95, 0),
      ],
      landmarks: [
        landmarkRD(95, 0, 'Blue Spawn Exit', 'checkpoint'),
        landmarkRD(0, 0, 'Raindance Bridge', 'sightline'),
        landmarkRD(-95, 0, 'Red Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [28, 42],
    },
    {
      id: 'rd-red-high',
      type: 'high',
      label: 'Red High',
      description: 'High arc over the west cliff rim, dropping onto the blue stand from above.',
      team: 'red',
      waypoints: [
        wpRD(-95, 0), wpRD(-80, 40), wpRD(-50, 70), wpRD(0, 80), wpRD(50, 65), wpRD(80, 30), wpRD(95, 0),
      ],
      landmarks: [
        landmarkRD(-95, 0, 'Red Spawn Exit', 'checkpoint'),
        landmarkRD(0, 80, 'West Rim Crest', 'sightline'),
        landmarkRD(95, 0, 'Blue Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 3,
      expectedSpeedRange: [34, 50],
    },
    {
      id: 'rd-blue-high',
      type: 'high',
      label: 'Blue High',
      description: 'East cliff high route with a sweeping dive toward the red stand.',
      team: 'blue',
      waypoints: [
        wpRD(95, 0), wpRD(80, -40), wpRD(50, -70), wpRD(0, -80), wpRD(-50, -65), wpRD(-80, -30), wpRD(-95, 0),
      ],
      landmarks: [
        landmarkRD(95, 0, 'Blue Spawn Exit', 'checkpoint'),
        landmarkRD(0, -80, 'East Rim Crest', 'sightline'),
        landmarkRD(-95, 0, 'Red Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 3,
      expectedSpeedRange: [34, 50],
    },
    {
      id: 'rd-ravine-sneak',
      type: 'low',
      label: 'Ravine Sneak',
      description: 'Low route through the ravine floor, using the deep channel for cover.',
      team: 'red',
      waypoints: [
        wpRD(-95, 20), wpRD(-60, 25), wpRD(-20, 15), wpRD(0, 0), wpRD(20, -15), wpRD(60, -25), wpRD(95, -20),
      ],
      landmarks: [
        landmarkRD(-95, 20, 'Red South Ledge', 'checkpoint'),
        landmarkRD(0, 0, 'Ravine Floor Crossing', 'cover'),
        landmarkRD(95, -20, 'Blue Stand Flank', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [24, 36],
    },
    {
      id: 'rd-blue-chase',
      type: 'chase',
      label: 'Blue Chase',
      description: 'Blue-side chase clipping the ravine wall and bridge approach.',
      team: 'blue',
      waypoints: [
        wpRD(95, 30), wpRD(65, 20), wpRD(30, 8), wpRD(0, 0), wpRD(-30, -8), wpRD(-65, -20), wpRD(-95, -30),
      ],
      landmarks: [
        landmarkRD(95, 30, 'Blue Low Exit', 'checkpoint'),
        landmarkRD(0, 0, 'Bridge Underpass', 'cover'),
        landmarkRD(-95, -30, 'Red Base Flank', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [30, 44],
    },
    {
      id: 'rd-generator-attack',
      type: 'stealth',
      label: 'Generator Attack',
      description: 'Stealth approach along the ravine wall toward the blue generator.',
      team: 'red',
      waypoints: [
        wpRD(-95, 0), wpRD(-80, -25), wpRD(-50, -40), wpRD(0, -30), wpRD(50, -20), wpRD(80, -10), wpRD(115, 0),
      ],
      landmarks: [
        landmarkRD(-95, 0, 'Red Spawn Exit', 'checkpoint'),
        landmarkRD(0, -30, 'Ravine Midpoint', 'sightline'),
        landmarkRD(115, 0, 'Blue Generator Door', 'generator-door'),
      ],
      difficultyRating: 3,
      expectedSpeedRange: [20, 34],
    },
    {
      id: 'rd-return-route',
      type: 'low',
      label: 'Return Route',
      description: 'Flag return hugging the cliff shelf back to the red stand.',
      team: 'blue',
      waypoints: [
        wpRD(95, 0), wpRD(70, -15), wpRD(40, -25), wpRD(0, -10), wpRD(-40, 15), wpRD(-70, 25), wpRD(-95, 0),
      ],
      landmarks: [
        landmarkRD(95, 0, 'Blue Stand Exit', 'checkpoint'),
        landmarkRD(0, -10, 'Return Shelf', 'cover'),
        landmarkRD(-95, 0, 'Red Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [24, 38],
    },
  ];
}

function buildKatabaticRoutes(): RouteMetadataMap[] {
  return [
    {
      id: 'red-main',
      type: 'main',
      label: 'Red Main',
      description: 'Red base to blue flag stand via mid saddle.',
      team: 'red',
      waypoints: [
        wpK(-78, 0), wpK(-55, -12), wpK(-30, -5), wpK(0, 25), wpK(25, 52), wpK(50, 62), wpK(78, 0),
      ],
      landmarks: [
        landmarkK(-78, 0, 'Red Spawn Exit', 'checkpoint'),
        landmarkK(-30, -5, 'Mid Saddle West', 'sightline'),
        landmarkK(78, 0, 'Blue Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [28, 42],
    },
    {
      id: 'blue-main',
      type: 'main',
      label: 'Blue Main',
      description: 'Blue base to red flag stand via mid saddle.',
      team: 'blue',
      waypoints: [
        wpK(78, 0), wpK(50, -62), wpK(25, -52), wpK(0, -25), wpK(-30, 5), wpK(-55, 12), wpK(-78, 0),
      ],
      landmarks: [
        landmarkK(78, 0, 'Blue Spawn Exit', 'checkpoint'),
        landmarkK(25, -52, 'Mid Saddle East', 'sightline'),
        landmarkK(-78, 0, 'Red Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [28, 42],
    },
    {
      id: 'red-high',
      type: 'high',
      label: 'Red High',
      description: 'Red base over north ridge to blue stand.',
      team: 'red',
      waypoints: [
        wpK(-78, 0), wpK(-60, 40), wpK(-30, 80), wpK(0, 110), wpK(30, 95), wpK(55, 50), wpK(78, 0),
      ],
      landmarks: [
        landmarkK(-78, 0, 'Red Spawn Exit', 'checkpoint'),
        landmarkK(0, 110, 'North Ridge Crest', 'sightline'),
        landmarkK(78, 0, 'Blue Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 3,
      expectedSpeedRange: [32, 48],
    },
    {
      id: 'blue-high',
      type: 'high',
      label: 'Blue High',
      description: 'Blue base over south ridge to red stand.',
      team: 'blue',
      waypoints: [
        wpK(78, 0), wpK(60, -40), wpK(30, -80), wpK(0, -105), wpK(-30, -90), wpK(-55, -45), wpK(-78, 0),
      ],
      landmarks: [
        landmarkK(78, 0, 'Blue Spawn Exit', 'checkpoint'),
        landmarkK(0, -105, 'South Ridge Crest', 'sightline'),
        landmarkK(-78, 0, 'Red Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 3,
      expectedSpeedRange: [32, 48],
    },
    {
      id: 'red-low',
      type: 'low',
      label: 'Red Low',
      description: 'Red base through secondary lane to blue stand.',
      team: 'red',
      waypoints: [
        wpK(-78, 30), wpK(-50, 20), wpK(-20, 18), wpK(10, 5), wpK(40, -20), wpK(65, -40), wpK(78, -15),
      ],
      landmarks: [
        landmarkK(-78, 30, 'Red South Exit', 'checkpoint'),
        landmarkK(-20, 18, 'Midfield Low Cover', 'cover'),
        landmarkK(78, -15, 'Blue Stand Flank', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [26, 38],
    },
    {
      id: 'blue-chase',
      type: 'chase',
      label: 'Blue Chase',
      description: 'Blue base chase route along secondary lane.',
      team: 'blue',
      waypoints: [
        wpK(78, -50), wpK(55, -30), wpK(30, -10), wpK(0, 5), wpK(-30, 15), wpK(-55, 25), wpK(-78, 30),
      ],
      landmarks: [
        landmarkK(78, -50, 'Blue Low Exit', 'checkpoint'),
        landmarkK(30, -10, 'Secondary Lane Mid', 'cover'),
        landmarkK(-78, 30, 'Red Base Flank', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [30, 44],
    },
    {
      id: 'red-stealth',
      type: 'stealth',
      label: 'Red Stealth',
      description: 'Red base generator approach via west spine.',
      team: 'red',
      waypoints: [
        wpK(-78, 0), wpK(-100, -25), wpK(-130, -40), wpK(-160, -30), wpK(-170, 0), wpK(-160, 30), wpK(-130, 40), wpK(-90, 20), wpK(-50, 10), wpK(0, 0), wpK(40, 10), wpK(78, 0),
      ],
      landmarks: [
        landmarkK(-78, 0, 'Red Spawn Exit', 'checkpoint'),
        landmarkK(-170, 0, 'Spine Midpoint', 'sightline'),
        landmarkK(78, 0, 'Blue Generator Door', 'generator-door'),
      ],
      difficultyRating: 3,
      expectedSpeedRange: [20, 34],
    },
    {
      id: 'red-heavy',
      type: 'heavy-assault',
      label: 'Red Heavy',
      description: 'Red base heavy assault route with vehicle lane.',
      team: 'red',
      waypoints: [
        wpK(-78, 0), wpK(-60, 5), wpK(-40, 10), wpK(-20, 18), wpK(0, 25), wpK(20, 35), wpK(40, 45), wpK(60, 30), wpK(78, 0),
      ],
      landmarks: [
        landmarkK(-78, 0, 'Red Spawn Exit', 'checkpoint'),
        landmarkK(0, 25, 'Midfield Crossing', 'sightline'),
        landmarkK(78, 0, 'Blue Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 1,
      expectedSpeedRange: [18, 28],
    },
  ];
}

function buildDangerousCrossingRoutes(): RouteMetadataMap[] {
  return [
    {
      id: 'dc-red-main',
      type: 'main',
      label: 'Red Main',
      description: 'Red base across the canyon to blue stand. Direct frontal assault through the central gorge.',
      team: 'red',
      waypoints: [
        wpDC(-120, 0), wpDC(-80, -10), wpDC(-40, -20), wpDC(0, 0), wpDC(40, 20), wpDC(80, 10), wpDC(120, 0),
      ],
      landmarks: [
        landmarkDC(-120, 0, 'Red Spawn Exit', 'checkpoint'),
        landmarkDC(-40, -20, 'Canyon West Rim', 'sightline'),
        landmarkDC(0, 0, 'Canyon Floor', 'checkpoint'),
        landmarkDC(120, 0, 'Blue Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [26, 40],
    },
    {
      id: 'dc-blue-main',
      type: 'main',
      label: 'Blue Main',
      description: 'Blue base across the canyon to red stand. Mirror route through the central gorge.',
      team: 'blue',
      waypoints: [
        wpDC(120, 0), wpDC(80, 10), wpDC(40, 20), wpDC(0, 0), wpDC(-40, -20), wpDC(-80, -10), wpDC(-120, 0),
      ],
      landmarks: [
        landmarkDC(120, 0, 'Blue Spawn Exit', 'checkpoint'),
        landmarkDC(40, 20, 'Canyon East Rim', 'sightline'),
        landmarkDC(0, 0, 'Canyon Floor', 'checkpoint'),
        landmarkDC(-120, 0, 'Red Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [26, 40],
    },
    {
      id: 'dc-canyon-high',
      type: 'high',
      label: 'Canyon High',
      description: 'High route along the canyon rim. Uses elevation for fast aerial approach with high risk.',
      team: 'red',
      waypoints: [
        wpDC(-120, 0), wpDC(-100, 50), wpDC(-60, 80), wpDC(0, 90), wpDC(60, 75), wpDC(100, 40), wpDC(120, 0),
      ],
      landmarks: [
        landmarkDC(-120, 0, 'Red Spawn Exit', 'checkpoint'),
        landmarkDC(-60, 80, 'North Ridge Approach', 'sightline'),
        landmarkDC(0, 90, 'Canyon Overlook', 'sightline'),
        landmarkDC(120, 0, 'Blue Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 3,
      expectedSpeedRange: [34, 50],
    },
    {
      id: 'dc-canyon-low',
      type: 'low',
      label: 'Canyon Low',
      description: 'Low route through the canyon floor. Tight corridors with cover but limited escape options.',
      team: 'red',
      waypoints: [
        wpDC(-120, -20), wpDC(-80, -30), wpDC(-40, -15), wpDC(0, -5), wpDC(40, 15), wpDC(80, 25), wpDC(120, 10),
      ],
      landmarks: [
        landmarkDC(-120, -20, 'Red South Exit', 'checkpoint'),
        landmarkDC(-40, -15, 'Canyon South Bend', 'cover'),
        landmarkDC(0, -5, 'Canyon Floor Low', 'cover'),
        landmarkDC(120, 10, 'Blue Stand Flank', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [24, 36],
    },
    {
      id: 'dc-blue-chase',
      type: 'chase',
      label: 'Blue Chase',
      description: 'Blue-side chase route crossing the canyon floor and clipping the south cover shelves.',
      team: 'blue',
      waypoints: [
        wpDC(120, 22), wpDC(86, 30), wpDC(45, 20), wpDC(4, 2), wpDC(-42, -18), wpDC(-84, -30), wpDC(-120, -18),
      ],
      landmarks: [
        landmarkDC(120, 22, 'Blue South Exit', 'checkpoint'),
        landmarkDC(4, 2, 'Canyon Floor Cut', 'cover'),
        landmarkDC(-120, -18, 'Red Stand Flank', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [28, 42],
    },
    {
      id: 'dc-generator-attack',
      type: 'stealth',
      label: 'Generator Attack',
      description: 'Red canyon-side route toward the blue generator socket with ridge cover.',
      team: 'red',
      waypoints: [
        wpDC(-120, -8), wpDC(-84, -28), wpDC(-45, -18), wpDC(0, -4), wpDC(48, 12), wpDC(92, 18), wpDC(145, 0),
      ],
      landmarks: [
        landmarkDC(-120, -8, 'Red Low Exit', 'checkpoint'),
        landmarkDC(0, -4, 'Canyon Generator Cut', 'cover'),
        landmarkDC(145, 0, 'Blue Generator Door', 'generator-door'),
      ],
      difficultyRating: 3,
      expectedSpeedRange: [20, 34],
    },
    {
      id: 'dc-heavy-lane',
      type: 'heavy-assault',
      label: 'Heavy Lane',
      description: 'Wide vehicle-friendly heavy route that favors sustained pressure over capper speed.',
      team: 'red',
      waypoints: [
        wpDC(-120, 0), wpDC(-90, 18), wpDC(-54, 34), wpDC(-12, 44), wpDC(34, 38), wpDC(82, 22), wpDC(120, 0),
      ],
      landmarks: [
        landmarkDC(-120, 0, 'Red Vehicle Shelf', 'checkpoint'),
        landmarkDC(-12, 44, 'North Vehicle Shelf', 'sightline'),
        landmarkDC(120, 0, 'Blue Front Door', 'checkpoint'),
      ],
      difficultyRating: 1,
      expectedSpeedRange: [18, 30],
    },
    {
      id: 'dc-return-route',
      type: 'low',
      label: 'Return Route',
      description: 'Flag return path through the safest canyon bend back toward friendly base.',
      team: 'blue',
      waypoints: [
        wpDC(120, 0), wpDC(86, -18), wpDC(46, -26), wpDC(4, -12), wpDC(-38, 10), wpDC(-82, 18), wpDC(-120, 0),
      ],
      landmarks: [
        landmarkDC(120, 0, 'Blue Stand Exit', 'checkpoint'),
        landmarkDC(4, -12, 'Return Bend', 'cover'),
        landmarkDC(-120, 0, 'Red Flag Stand', 'checkpoint'),
      ],
      difficultyRating: 2,
      expectedSpeedRange: [24, 38],
    },
  ];
}

export const MAPS: Record<MapId, MapData> = {
  katabatic: {
    id: 'katabatic',
    label: 'Katabatic',
    boundsRadius: 380,
    description: 'Classic rolling terrain with broad basins, sculpted routes, and open sightlines. The original proving ground.',
    weather: 'snow',
    timeOfDay: 'day',
    terrainPalette: 'arctic',
    briefing: {
      factionLeft: 'Blood Eagle',
      factionRight: 'Diamond Sword',
      blurb: 'Open snowfields and rolling basins. Speed is law — chain ski lines and never break momentum.',
      recommendedLoadout: 'Light armor · Spinfusor · Grenade Launcher',
    },
    haze: { tint: '#9fc6e0', opacity: 0.32 },
    visualProfile: {
      terrain: {
        snow: '#f6fbff',
        ice: '#9ad9ff',
        rock: '#26343f',
        route: '#dcf5ff',
      },
      sky: {
        // Phase 61 — T2 sky palette: deeper blue zenith, bright cyan-white horizon, warm yellow sun
        fog: '#aac8e0',
        ambient: '#88aac4',
        hemisphereSky: '#1a3a5e',
        hemisphereGround: '#0c1520',
        sun: '#ffe680',
        horizon: '#aac8e0',
      },
      routeTone: '#62ddff',
      landmarkTone: '#edf7ff',
      baseSilhouette: 'compact-spires',
    },
    flagPositions: {
      red: anchorK(-78, 0),
      blue: anchorK(78, 0),
    },
    spawnPositions: {
      red: [anchorK(-58, -22), anchorK(-58, 22), anchorK(-70, 0)],
      blue: [anchorK(58, 22), anchorK(58, -22), anchorK(70, 0)],
    },
    spawnViewTargets: {
      red: [wpK(-22, -6), wpK(-22, 6), wpK(-18, 0)],
      blue: [wpK(22, 6), wpK(22, -6), wpK(18, 0)],
    },
    generatorPositions: {
      red: socketK(-101, 0, 1.15),
      blue: socketK(101, 0, 1.15),
    },
    routes: buildKatabaticRoutes(),
    baseProfile: {
      elevation: 1,
      spread: 1,
    },
  },
  dangerouscrossing: {
    id: 'dangerouscrossing',
    label: 'Dangerous Crossing',
    boundsRadius: 360,
    description: 'A deep canyon splits the field. Wider bases, sharper ridges, and fewer routes demand precision.',
    weather: 'sand',
    timeOfDay: 'sunset',
    terrainPalette: 'desert',
    briefing: {
      factionLeft: 'Phoenix',
      factionRight: 'Starwolf',
      blurb: 'Sun-baked canyon walls and shifting sand. Visibility is short — push lanes together and watch the ridges.',
      recommendedLoadout: 'Medium armor · Chaingun · Mortar',
    },
    haze: { tint: '#d4a070', opacity: 0.4 },
    visualProfile: {
      terrain: {
        snow: '#d9edf7',
        ice: '#70b7d7',
        rock: '#3a2f38',
        route: '#ffd6a6',
      },
      sky: {
        fog: '#211923',
        ambient: '#aa8f7a',
        hemisphereSky: '#d6b08d',
        hemisphereGround: '#1a1017',
        sun: '#ffd8ad',
        horizon: '#d0956e',
      },
      routeTone: '#ffcc66',
      landmarkTone: '#ffd6a6',
      baseSilhouette: 'canyon-shelves',
    },
    flagPositions: {
      red: anchorDC(-120, 0),
      blue: anchorDC(120, 0),
    },
    spawnPositions: {
      red: [anchorDC(-98, -26), anchorDC(-98, 26), anchorDC(-114, 0)],
      blue: [anchorDC(98, 26), anchorDC(98, -26), anchorDC(114, 0)],
    },
    spawnViewTargets: {
      red: [wpDC(-34, -8), wpDC(-34, 8), wpDC(-40, 0)],
      blue: [wpDC(34, 8), wpDC(34, -8), wpDC(40, 0)],
    },
    generatorPositions: {
      red: socketDC(-145, 0, 1.15),
      blue: socketDC(145, 0, 1.15),
    },
    routes: buildDangerousCrossingRoutes(),
    baseProfile: {
      elevation: 1.6,
      spread: 1.5,
    },
  },
  raindance: {
    id: 'raindance',
    label: 'Raindance',
    boundsRadius: 340,
    description: 'A windswept ravine split by a narrow bridge. Verticality dominates every engagement.',
    weather: 'rain',
    timeOfDay: 'night',
    terrainPalette: 'verdant',
    briefing: {
      factionLeft: 'Diamond Sword',
      factionRight: 'Blood Eagle',
      blurb: 'Storm-lit ravine with a single bridge across the gap. Own the high ground or get punished from above.',
      recommendedLoadout: 'Medium armor · Spinfusor · Plasma',
    },
    haze: { tint: '#5a8a6a', opacity: 0.34 },
    visualProfile: {
      terrain: {
        snow: '#e8ddd0',
        ice: '#c4b8a8',
        rock: '#5a4a3a',
        route: '#ffd6a6',
      },
      sky: {
        fog: '#1a1510',
        ambient: '#b89a7a',
        hemisphereSky: '#d4b898',
        hemisphereGround: '#1a1008',
        sun: '#ffd8a0',
        horizon: '#c09060',
      },
      routeTone: '#ffaa55',
      landmarkTone: '#ffe8c8',
      baseSilhouette: 'canyon-shelves',
    },
    flagPositions: {
      red: anchorRD(-95, 0),
      blue: anchorRD(95, 0),
    },
    spawnPositions: {
      red: [anchorRD(-75, -20), anchorRD(-75, 20), anchorRD(-65, 0)],
      blue: [anchorRD(75, 20), anchorRD(75, -20), anchorRD(65, 0)],
    },
    spawnViewTargets: {
      red: [wpRD(-30, -10), wpRD(-30, 10), wpRD(-25, 0)],
      blue: [wpRD(30, 10), wpRD(30, -10), wpRD(25, 0)],
    },
    generatorPositions: {
      red: socketRD(-115, 0, 1.15),
      blue: socketRD(115, 0, 1.15),
    },
    routes: buildRaindanceRoutes(),
    baseProfile: {
      elevation: 1.8,
      spread: 1.4,
    },
  },
};

export interface SiegeControlPoint {
  id: string;
  name: string; // "Alpha", "Beta", "Gamma"
  position: { x: number; y: number; z: number };
  captureRadius: number; // 6
}

export const SIEGE_CONTROL_POINTS: Record<MapId, SiegeControlPoint[]> = {
  katabatic: [
    { id: 'alpha', name: 'Alpha', position: { x: -40, y: 15, z: -60 }, captureRadius: 6 },
    { id: 'beta',  name: 'Beta',  position: { x: 0,   y: 0,  z: 0   }, captureRadius: 6 },
    { id: 'gamma', name: 'Gamma', position: { x: 40,  y: 15, z: 60  }, captureRadius: 6 },
  ],
  dangerouscrossing: [
    { id: 'alpha', name: 'Alpha', position: { x: -50, y: 8,  z: -30 }, captureRadius: 6 },
    { id: 'beta',  name: 'Beta',  position: { x: 0,   y: 2,  z: 10  }, captureRadius: 6 },
    { id: 'gamma', name: 'Gamma', position: { x: 50,  y: 8,  z: 30  }, captureRadius: 6 },
  ],
  raindance: [
    { id: 'alpha', name: 'Alpha', position: { x: -35, y: 6,  z: -45 }, captureRadius: 6 },
    { id: 'beta',  name: 'Beta',  position: { x: 5,   y: 1,  z: 0   }, captureRadius: 6 },
    { id: 'gamma', name: 'Gamma', position: { x: 35,  y: 6,  z: 45  }, captureRadius: 6 },
  ],
};

export type SiegePointState = 'neutral' | 'contested' | 'team1' | 'team2';

export interface SiegeGameState {
  points: Record<string, SiegePointState>; // pointId -> state
  captureProgress: Record<string, number>; // pointId -> 0..1
  teamScores: { team1: number; team2: number };
  winScore: number; // 500
}

export function getMapData(id: MapId): MapData {
  return MAPS[id];
}

export function getMapVisualProfile(id: MapId): MapData['visualProfile'] {
  return MAPS[id].visualProfile;
}
